import type {
  PickupAnalysisOpportunityAttempt,
  PickupEligibleProduct,
  PricingWaterfall
} from './types';
import {
  buildPickupInvestorPricingRows,
  pickupInvestorPricingInvestorCount,
  type PickupInvestorPricingContext,
  type PickupInvestorPricingRow
} from './pickupInvestorPricing';
import { pickupDeltaPoints, pickupPointsToDollars } from './pickupPricingMath';

export interface PickupPricingRunSource {
  runId: string;
  analyzedAt: string;
  lockedPrice: number;
  lockedInvestorPrice: number | null;
  currentInvestorPrice: number | null;
  currentInvestor: string | null;
  loanAmount: number | null;
  basisLockPeriod: number | null;
  products: PickupEligibleProduct[];
  pricingWaterfall: PricingWaterfall | null;
}

export interface PickupPricingRun extends PickupPricingRunSource {
  latest: boolean;
  rows: PickupInvestorPricingRow[];
  bestProduct: PickupEligibleProduct | null;
  bestPrice: number | null;
  bestPoints: number | null;
  bestGainDollars: number | null;
  investorCount: number;
  priorRunDelta: number | null;
}

function sourceTimestamp(source: PickupPricingRunSource): number {
  const parsed = Date.parse(source.analyzedAt);
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

function runContext(
  source: PickupPricingRunSource,
  opportunityAttempts: PickupAnalysisOpportunityAttempt[]
): PickupInvestorPricingContext {
  return {
    lockedPrice: source.lockedPrice,
    lockedInvestorPrice: source.lockedInvestorPrice,
    currentInvestorPrice: source.currentInvestorPrice,
    currentInvestor: source.currentInvestor,
    loanAmount: source.loanAmount,
    analyzedAt: source.analyzedAt,
    basisLockPeriod: source.basisLockPeriod,
    eligibleProducts: source.products,
    pricingWaterfall: source.pricingWaterfall,
    opportunityAttempts
  };
}

/**
 * Builds the bounded pricing history newest-first. Only the newest run receives
 * candidate ranks and recorded attempt outcomes; older evidence is immutable and
 * deliberately non-actionable.
 */
export function buildPickupPricingRuns(
  sources: PickupPricingRunSource[],
  opportunityAttempts: PickupAnalysisOpportunityAttempt[] = []
): PickupPricingRun[] {
  const ordered = [...sources].sort((left, right) => (
    sourceTimestamp(right) - sourceTimestamp(left)
    || right.runId.localeCompare(left.runId)
  ));
  const runs = ordered.map((source, index) => {
    const latest = index === 0;
    const rows = buildPickupInvestorPricingRows(
      runContext(source, latest ? opportunityAttempts : [])
    ).map((row) => latest ? row : {
      ...row,
      rank: null,
      attemptStatus: null
    });
    const bestRow = rows[0] ?? null;
    const bestPrice = bestRow?.price ?? null;
    const bestPoints = pickupDeltaPoints(bestPrice, source.lockedPrice);
    const bestGainDollars = bestPoints !== null && bestPoints > 0
      ? pickupPointsToDollars(bestPoints, source.loanAmount)
      : null;

    return {
      ...source,
      latest,
      rows,
      bestProduct: bestRow?.product ?? null,
      bestPrice,
      bestPoints,
      bestGainDollars,
      investorCount: pickupInvestorPricingInvestorCount(rows),
      priorRunDelta: null
    };
  });

  return runs.map((run, index) => {
    const olderRun = runs[index + 1];
    return {
      ...run,
      priorRunDelta: olderRun && run.bestPrice !== null && olderRun.bestPrice !== null
        ? pickupDeltaPoints(run.bestPrice, olderRun.bestPrice)
        : null
    };
  });
}
